import Vue from 'vue';
import pushid from 'pushid';

function exists(input) {
    if (typeof input === 'function') {
        try {
            input = input();
            return this.x(input);
        } catch (e) {
            return false;
        }
    }
    if (
        input === null ||
        input === false ||
        (typeof input === 'string' && input.trim() == '') ||
        (typeof input === 'object' &&
            Object.keys(input).length === 0 &&
            input.constructor === Object) ||
        typeof input === 'undefined' ||
        (Array.isArray(input) && input.length === 0) ||
        (Array.isArray(input) && input.length === 1 && input[0] === '')
    ) {
        return false;
    }
    return true;
}
export { exists };

function slugify(text) {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .split('ä')
        .join('ae')
        .split('ö')
        .join('oe')
        .split('ü')
        .join('ue')
        .split('ß')
        .join('ss')
        .split('/')
        .join('-')
        .replace(/[^\w\s-]/g, '') // remove non-word [a-z0-9_], non-whitespace, non-hyphen characters
        .replace(/[\s_-]+/g, '-') // swap any length of whitespace, underscore, hyphen characters with a single -
        .replace(/^-+|-+$/g, ''); // remove leading, trailing -
}
export { slugify };

function toArray(obj) {
    let arr = [];
    for (let prop in obj) {
        if (obj.hasOwnProperty(prop)) {
            arr.push(obj[prop]);
        }
    }
    return arr;
}
export { toArray };

function findRecursiveInObject(object, key = null, value = null, path = '', paths = []) {
    if (object !== null && typeof object === 'object') {
        for (const [object__key, object__value] of Object.entries(object)) {
            if (object__value !== null && typeof object__value === 'object') {
                findRecursiveInObject(
                    object__value,
                    key,
                    value,
                    (path === '' ? '' : path + '.') + object__key,
                    paths
                );
            } else if (
                (key === null || object__key === key) &&
                (value === null || object__value === value)
            ) {
                paths.push(path);
                break; // only take first
            }
        }
    }
    return paths;
}
export { findRecursiveInObject };

function shuffle(array) {
    let currentIndex = array.length,
        temporaryValue,
        randomIndex;
    while (0 !== currentIndex) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
    }
    return array;
}
export { shuffle };

function random(a, b = null) {
    if (b === null && a !== undefined && a !== null && a !== '' && Array.isArray(a)) {
        return a[Math.floor(Math.random() * a.length)];
    }
    return ~~(Math.random() * (b - a + 1)) + a;
}
export { random };

function debounce(func, wait, immediate) {
    var timeout;
    return function() {
        var context = this,
            args = arguments;
        var later = function() {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}
export { debounce };

function formatGermanDate(date) {
    let d = new Date(date);
    return (
        ('0' + d.getDate()).slice(-2) +
        '.' +
        ('0' + (d.getMonth() + 1)).slice(-2) +
        '.' +
        d.getFullYear()
    );
}
export { formatGermanDate };

let lastId = null;
function getRandomId() {
    lastId = pushid();
    return lastId;
}
export { getRandomId };

function getLastId() {
    return lastId;
}
export { getLastId };

/* convenience methods from notifications */
function success(text) {
    Vue.notify({ type: 'success', text: text });
}
function warn(text) {
    Vue.notify({ type: 'warn', text: text });
}
function error(text) {
    Vue.notify({ type: 'error', text: text });
}
export { success, warn, error };

function getEnvironment() {
    if (window.location.host.indexOf('local') > -1) {
        return 'development';
    }
    if (process.env.NODE_ENV === 'production' && window.location.host.indexOf('test') > -1) {
        return 'testing';
    }
    if (process.env.NODE_ENV === 'production') {
        return 'production';
    }
    return 'development';
}
export { getEnvironment };

function offsetTop(el) {
    let val;
    // offset of element inside container
    val =
        el.getBoundingClientRect().top +
        document.querySelector('.page-wrap').scrollTop -
        document.documentElement.clientTop;
    // offset of container
    val -=
        document.querySelector('.page-wrap').getBoundingClientRect().top +
        window.pageYOffset -
        document.documentElement.clientTop;
    return val;
}
export { offsetTop };

/* convenience method for api calls */
function api(domain, path, method, data = null) {
    /* fetch returns a promise which only fails (inside catch) on network error */
    /* we want to combine all errors (also from the api) in a fetcher function */
    /* also we provide some convenience shortcuts to abstract away the js fetch api */
    return new Promise((resolve, reject) => {
        if (domain === 'deha-api') {
            domain = process.env.VUE_APP_API_DEHA_URL;
        } else if (domain === 'tecpage-api') {
            domain = process.env.VUE_APP_API_TECPAGE_URL;
        }
        let url = domain + path,
            args = {
                method: method,
                cache: 'no-cache',
                headers: { 'content-type': 'application/json' }
            };
        if (data !== null) {
            args.body = JSON.stringify(data);
        }
        Vue.prototype.$api
            .fetch(url, args)
            .then((result) => {
                return result.json();
            })
            .catch((error) => {
                return { success: false, message: error };
            })
            .then((data) => {
                if (
                    data === undefined ||
                    data === '' ||
                    data === null ||
                    typeof data !== 'object' ||
                    data.success === false
                ) {
                    reject(data);
                } else {
                    resolve(data);
                }
            });
    });
}
export { api };

function isTouchDevice() {
    /* identify touch device */
    /* source: https://stackoverflow.com/a/4819886/204558 */
    var prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
    var mq = function(query) {
        return window.matchMedia(query).matches;
    };
    if (
        'ontouchstart' in window ||
        (window.DocumentTouch && document instanceof window.DocumentTouch)
    ) {
        return true;
    }
    // include the 'heartz' as a way to have a non matching MQ to help terminate the join
    // https://git.io/vznFH
    var query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
    return mq(query);
}
export { isTouchDevice };

function prevAll(elem, filter) {
    var sibs = [];
    while ((elem = elem.previousSibling)) {
        if (elem.nodeType === 3) continue; // ignore text nodes
        if (!filter || filter(elem)) sibs.push(elem);
    }
    return sibs;
}
export { prevAll };

function compareDates(d1, d2) {
    // safari has problems in parsing "2019-01-01 00:00:00"
    if (typeof d1 === 'string') {
        d1 = d1.split(' ').join('T');
    }
    if (typeof d2 === 'string') {
        d2 = d2.split(' ').join('T');
    }
    d1 = new Date(d1);
    d2 = new Date(d2);
    d1.setHours(0);
    d1.setMinutes(0);
    d1.setSeconds(0);
    d1.setMilliseconds(0);
    d2.setHours(0);
    d2.setMinutes(0);
    d2.setSeconds(0);
    d2.setMilliseconds(0);
    if (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    ) {
        return 0;
    }
    if (d1 < d2) {
        return -1;
    }
    return 1;
}
export { compareDates };
